import { Box, Flex, IconButton, Link, Text } from '@chakra-ui/react';
import { useCallback, useMemo, useState } from 'react';
import { useAnnouncementsQuery } from '@/hooks/queries/use-announcements-query';
import type { PlatformAnnouncement, PlatformAnnouncementStatus } from '@fluenceur/types';
import { createDismissHelpers } from './dismissal';

const DOT_COLOR: Record<PlatformAnnouncementStatus, string> = {
  info: 'blue.500',
  degraded: 'yellow.500',
  maintenance: 'purple.500',
  outage: 'orange.500',
};

const BG: Record<PlatformAnnouncementStatus, string> = {
  info: 'bg.muted',
  degraded: 'bg.muted',
  maintenance: 'bg.muted',
  outage: 'orange.50',
};

const { isDismissed, dismiss } = createDismissHelpers('platform_announcement_');

function isSafeIncidentLink(value: string | null): value is string {
  if (!value) return false;
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
}

export default function PlatformIncidentBanner({ brandId = null }: { brandId?: string | null }) {
  const { data: announcements } = useAnnouncementsQuery(brandId);
  const [runtimeDismissed, setRuntimeDismissed] = useState<Set<string>>(new Set());

  const active = useMemo(() => {
    for (const a of announcements ?? []) {
      if (a.kind !== 'platform') continue;
      if (runtimeDismissed.has(a.id)) continue;
      if (isDismissed(a.id)) continue;
      return a as PlatformAnnouncement;
    }
    return null;
  }, [announcements, runtimeDismissed]);

  const handleDismiss = useCallback((id: string) => {
    dismiss(id);
    setRuntimeDismissed((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  if (!active) return null;

  return (
    <Flex
      align="center"
      justify="center"
      gap={2}
      px={4}
      py={2}
      bg={BG[active.status]}
      borderBottomWidth="1px"
      borderColor="border.muted"
      fontSize="13px"
      color="fg"
    >
      <Box
        data-testid="platform-incident-dot"
        data-status={active.status}
        w={2}
        h={2}
        borderRadius="full"
        bg={DOT_COLOR[active.status]}
        flexShrink={0}
      />
      <Text fontWeight="600">{active.title}</Text>
      <Text color="fg.muted">- {active.description}</Text>
      {isSafeIncidentLink(active.linkUrl) && (
        <Link
          href={active.linkUrl}
          target="_blank"
          rel="noopener noreferrer"
          textDecoration="underline"
          color="fg"
        >
          {active.linkLabel ?? 'Learn more'}
        </Link>
      )}
      {active.dismissible && (
        <IconButton
          aria-label="Dismiss announcement"
          size="xs"
          variant="ghost"
          onClick={() => handleDismiss(active.id)}
          ml="auto"
        >
          x
        </IconButton>
      )}
    </Flex>
  );
}
